import { useState, useEffect } from "react";
import { isMobile } from 'react-device-detect';
import { useParams, useSearchParams } from "react-router-dom";
import useWindowDimensions from "../hooks/windowDimensions";
import useFetchWithMsal from "../hooks/useFetchWithMsal";
import { protectedResources } from "../authConfig";
import { ISkillLevel } from "../model/SkillLevel.model";
import { ISkillPath } from "../model/SkillPath.model";
import { ICompetencyMgmtPeriod } from "../model/CompetencyMgmtPeriod.model";
import { IEmployeeProgression } from "../model/EmployeeProgression.model";
import CompetencyPeriodSelector from "../components/CompetencyPeriodSelector/CompetencyPeriodSelector";
import LevelIcon from "../components/skillpath/LevelIcon";
import SkillLevelCheckboxes from "../components/skillpath/SkillLevelCheckboxes";
import Tooltip from "../components/Tooltip/Tooltip";
import Loading from "../components/Loading/Loading";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/Accordion/Accordion";
import { Icon } from "../components/icons/icons";
import parse from 'html-react-parser';
import '../styles/pages/EmployeeProgression.scss';
import '../styles/pages/MyCompetencies.scss';
import '../components/skillpath/MobileSkillView.scss';
import Modal from "../components/Modal/Modal";
import AddCompetency from "../components/skillpath/AddCompetency";
import { IEmployee } from "../model/Employee.mode";
import { toast } from 'react-toastify';

const SupervisorEmpCompetencies = () => {
  const { upn } = useParams<{ upn: string }>();
  const [searchParams] = useSearchParams();
  const initialPeriodId = searchParams.get('periodId') ?? undefined;

  const { execute } = useFetchWithMsal({
    scopes: protectedResources.apiCFSkillPath.scopes.read,
  });

  const [isMobileClient, setIsMobileClient] = useState<boolean>(false);
  const { width } = useWindowDimensions();
  useEffect(() => {
    setIsMobileClient(isMobile || width < 1100);
  }, [width]);

  const [currentEmp, setCurrentEmp] = useState<IEmployee | null>(null);
  const [selectedPeriod, setSelectedPeriod] = useState<ICompetencyMgmtPeriod | null>(null);
  const [selectedPeriodId, setSelectedPeriodId] = useState<string>('');
  const [skillTree, setSkillTree] = useState<ISkillPath[]>([]);
  const [skillTreeLoaded, setSkillTreeLoaded] = useState<boolean>(false);
  const [progressions, setProgressions] = useState<IEmployeeProgression[]>([]);
  const [progressionLevels, setProgressionLevels] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedEmployeeCompetencyId, setSelectedEmployeeCompetencyId] = useState<string | null>(null);


  useEffect(() => {
    if (!upn) {
      setLoading(false);
      return;
    }
    setLoading(true);
    Promise.all([
      execute("GET", `/api/SupEmpProg/SkillTree/${encodeURIComponent(upn)}`, undefined),
      execute("GET", `/api/SupEmpProg/List/${encodeURIComponent(upn)}`, undefined),
    ]).then(([empResponse, progResponse]: any[]) => {
      if (empResponse?.employee) {
        setCurrentEmp(empResponse.employee);
      }
      if (progResponse) {
        setProgressions(progResponse.progressions ?? []);
        setProgressionLevels(progResponse.levels ?? []);
      }
    })
    .catch((err)=> {
      toast.error(err?.error ?? err?.message ?? "An error occurred.");
    })
    .finally(() => setLoading(false));
  }, [upn]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadCompetencies = (upn: string) => {
     execute("GET", `/api/EmpCompetency/skillTree/${encodeURIComponent(upn)}/${selectedPeriodId}`, undefined)
      .then((response: any) => {
        if (!response) return;
        setSkillTree(flattenSkillTree(response.skillPaths ?? []));
        setSkillTreeLoaded(true);
      })
      .catch((err)=> {toast.error(err?.error ?? err?.message ?? "An error occurred.");})
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    if (!upn || !selectedPeriodId || skillTreeLoaded) return;
    setLoading(true);
    loadCompetencies(upn);
  }, [upn, selectedPeriodId, skillTreeLoaded, execute]); // eslint-disable-line react-hooks/exhaustive-deps

  const flattenSkillTree = (skillPaths: any[]): ISkillPath[] => {
    const flat: ISkillPath[] = [];
    skillPaths.forEach(sp => {
      sp.skillGroups?.forEach((sg: any) => {
        sg.skills?.forEach((s: any) => {
          const sp1: any = JSON.parse(JSON.stringify(sp));
          const sg1: any = JSON.parse(JSON.stringify(sg));
          sp1.skillGroups = [sg1];
          sg1.skills = [s];
          flat.push(sp1);
        });
      });
    });
    return flat;
  };

  const getDividerText = (pathName: string, groupName: string, skillName: string) =>
    `${pathName} : ${groupName} : ${skillName}`;

  const getSkillDetails = (skill: any, level: ISkillLevel) => {
    if (!Array.isArray(skill.details)) return null;
    return skill.details
      .filter((sd: any) => sd.skillLevelId === level.skillLevelId)
      .map((detail: any, i: number) => (
        <div key={i} className='m-t--75 m-b--75'>
          {parse((detail.skillText + "").replaceAll('<a href=', '<a target="_blank" rel="noopener noreferrer" href='))}
        </div>
      ));
  };

  const getLevelIconName = (indexNumber: number) => {
    if (indexNumber === 1) return 'icon-novice';
    if (indexNumber === 2) return 'icon-mid-level';
    return 'icon-advanced';
  };

  const getAccordionColor = (skillPathId: any) => {
    switch (skillPathId) {
      case 1: return "secondary";
      case 2: return "orange";
      case 3: return "purple";
      case 4: return "yellow";
      case 5: return "green";
      default: return "orange";
    }
  };

  const handlePeriodChange = (period: ICompetencyMgmtPeriod) => {
    setSelectedPeriod(period);
    setSelectedPeriodId(period.competencyMgmtPeriodId);
    setSkillTree([]);
    setSkillTreeLoaded(false);
  };

 
  const handleDelete = (employeeCometencyId: string) => { 
    console.log(employeeCometencyId);
      setSelectedEmployeeCompetencyId(employeeCometencyId);
      setShowDeleteModal(true);
    };

   const confirmDelete = () => {
          if (!selectedEmployeeCompetencyId) return;
          setShowDeleteModal(false);
          setLoading(true);
          execute("DELETE", `/api/EmpCompetency/remove/${selectedEmployeeCompetencyId}`)
              .then(() => {
                  
                  if(currentEmp)
                  {
                  loadCompetencies(currentEmp.userPrincipalName)};
                  toast.success('Competency deleted successfully.');
              })
              .catch((err: any) => {                
                  toast.error(err?.error ?? err?.message ?? "An error occurred.");
              })
              .finally(()=> {setSelectedEmployeeCompetencyId("");setLoading(false);});
      };
 const handleAdd = () => {      
      setShowAddModal(true);
    };

  const doAdd = (skillId: string) => {
    if (!currentEmp) return;
    setShowAddModal(false);
    setLoading(true);
    execute("POST", '/api/EmpCompetency/add', {
      userPrincipalName: currentEmp.userPrincipalName,
      competencyMgmtPeriodId: selectedPeriodId,
      skillId
    })
    .then(() => {
        loadCompetencies(currentEmp.userPrincipalName);
        toast.success('Competency added successfully.');
    })
    .catch((err: any) => {
        toast.error(err?.error ?? err?.message ?? "An error occurred.");
    })
    .finally(()=> {setLoading(false);});
  }

  const isPeriodEditable = !!selectedPeriod
    && new Date(selectedPeriod.startDate) <= new Date()
    && new Date() <= new Date(selectedPeriod.endDate);

  const existingSkillIds = skillTree.flatMap((sp: any) =>
    sp.skillGroups?.flatMap((sg: any) => sg.skills?.map((s: any) => `${s.SkillId}`) ?? []) ?? []
  );
  
   const formatDateMonthDay = (dateStr: string | undefined): string => {
    if(dateStr === undefined){
      return "the end of the editing period";
    }
      const fmt = (dateStr: string, includeYear: boolean) => {
        const d = new Date(dateStr);
        const month = d.toLocaleString('default', { month: 'long', timeZone: 'UTC' });
        const day = d.getUTCDate();
        return includeYear ? `${month} ${day}, ${d.getUTCFullYear()}` : `${month} ${day}`;
      };
      return `${fmt(dateStr, false)}`;
    };

  const getMobileHeader = () => (
    <section className='bg-primary p-2 clr-white'>
      <div className="dis-grid gap-1">
        <h2 className="fnt-wght-bold m-b-1 m-t-0 h1-size">
          Competencies{currentEmp ? `: ${currentEmp.fullName}` : ''}
        </h2>
        <div className="dis-flex dis-flex-rows dis-flex-aln-center gap-1">
          <CompetencyPeriodSelector initialPeriodId={initialPeriodId} onPeriodChange={handlePeriodChange} />

        </div>
      </div>
      <div className="m-t-1">
        <Accordion>
          <AccordionItem id="mobile-competency-guidance">
            <AccordionTrigger color="primary" headingLevel={3}>
              Guidance for Managing Employee Competencies
            </AccordionTrigger>
            <AccordionContent color="primary">
              <p className="m-t-0 m-b-1"><b>💡 Tip:</b> Competencies should reflect the employee's current responsibilities and development priorities. Consider adjusting competencies as skills, roles, or business needs evolve.</p>
              <ul className="reset">
                <li className="ep-bookmark-li">
                  <Icon name="icon-chat-bullet-white" className="ep-icon-chat" />
                  <p className="m-t--50 m-b--50">Review assigned competencies for the selected review period.</p>
                </li>
                <li className="ep-bookmark-li">
                  <Icon name="icon-pencil" className="clr-white ep-icon-chat" />
                  <p className="m-t--50 m-b--50">Update competencies as needed for the next review period by adding, removing, or replacing competencies to align with the employee's role, development goals, and performance expectations.</p>
                </li>
                <li className="ep-bookmark-li">
                  <Icon name="icon-chat-bullet-white" className="ep-icon-chat" />
                  <p className="m-t--50 m-b--50">Select up to 8 competencies that will be evaluated during the performance review period. (6 is recommended.)</p>
                </li>
                <li className="ep-bookmark-li">
                  <Icon name="icon-chat-bullet-white" className="ep-icon-chat" />
                  <p className="m-t--50 m-b--50">Complete all competency updates by {formatDateMonthDay(selectedPeriod?.endDate)} to ensure employees have adequate time to understand expectations and demonstrate proficiency before the next review cycle.</p>
                </li>
              </ul>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>
    </section>
  );

  const getDesktopHeader = () => (
    <div className="core-container desktop">
      <div className="core-desktop-max-width padded">
        <div className="ep-desktop-heading-ul-container">
          <div>
            <h2 className="fnt-wght-bold m-b--50 m-t-0 h1-size">
              Competencies{currentEmp ? `: ${currentEmp.fullName}` : ''}
            </h2>

            <div className="dis-flex dis-flex-rows dis-flex-aln-start gap-1">
              <CompetencyPeriodSelector initialPeriodId={initialPeriodId} onPeriodChange={handlePeriodChange} />
              <p className="m-t-1 m-b--50"><b>💡 Tip:</b> Competencies should reflect the employee's current responsibilities and development priorities. Consider adjusting competencies as skills, roles, or business needs evolve.</p>
            </div>
          </div>
          <div className="">
            <h3 className="fnt-wght-bold m-b--50 m-t-0">Managing Employee Competencies</h3>
            <ul className="reset">
              <li className="ep-bookmark-li">
                <Icon name="icon-chat-bullet-white" className="ep-icon-chat" />
                <p className="m-t--50 m-b--50">Review assigned competencies for the selected review period.</p>
              </li>
              <li className="ep-bookmark-li">
                <Icon name="icon-pencil" className="clr-white ep-icon-chat" />
                <p className="m-t--50 m-b--50">Update competencies as needed for the next review period by adding, removing, or replacing competencies to align with the employee's role, development goals, and performance expectations.</p>
              </li>
              <li className="ep-bookmark-li">
                <Icon name="icon-chat-bullet-white" className="ep-icon-chat" />
                <p className="m-t--50 m-b--50">Select up to 8 competencies that will be evaluated during the performance review period. (6 is recommended.)</p>
              </li>
              <li className="ep-bookmark-li">
                <Icon name="icon-chat-bullet-white" className="ep-icon-chat" />
                <p className="m-t--50 m-b--50">Complete all competency updates by {formatDateMonthDay(selectedPeriod?.endDate)} to ensure employees have adequate time to understand expectations and demonstrate proficiency before the next review cycle.</p>
              </li>
            </ul>
            
          </div>
        </div>
      </div>
    </div>
  );

 
  return (
    <>
      <Modal isOpen={showDeleteModal} modalTitle="Remove Competency" onClose={() => setShowDeleteModal(false)}>
        <p className="modal-form-content-scroll-container">Are you sure you want to remove this competency?</p>
        <div className="modal-form-button-container two-button">
            <button className="btn-med-gray" onClick={() => setShowDeleteModal(false)}>No</button>
            <button className="btn-secondary" onClick={confirmDelete}>Yes</button>
        </div>
      </Modal>
      <Modal isOpen={showAddModal} modalTitle="Add Competency" onClose={() => setShowAddModal(false)}>
        <AddCompetency
          existingSkillIds={existingSkillIds}
          onAdd={doAdd}
          onCancel={() => setShowAddModal(false)}
        />
      </Modal>

      <div className={isMobileClient ? "mobile" : undefined}>
      {isMobileClient ? getMobileHeader() : getDesktopHeader()}

      {skillTreeLoaded && skillTree.length === 0 && (
        <div className="core-desktop-max-width padded">
          <p>No competencies assigned for this period.</p>
        </div>
      )}

      {skillTree.map((skillPath: any, spIdx: number) =>
        skillPath.skillGroups?.map((sg: any, sgIdx: number) =>
          sg.skills?.map((skill: any) => (
            <div className="">
            <Accordion key={`${skill.SkillId}-${spIdx}-${sgIdx}`}>
              <AccordionItem id={skill.SkillId} key={skill.SkillId}>
                <AccordionTrigger color={getAccordionColor(skillPath.skillPathId)} headingLevel={3}
                 action={
                   isPeriodEditable ? (
                     <button className="btn-outlined-primary dis-flex dis-flex-rows dis-flex-aln-center gap--25"
                      style={{ padding: '4px 12px', fontSize: '0.85rem' }} onClick={(e) => {handleDelete(skill.employeeCompetencyId); }}>
                      <span>Remove</span>
                    </button>
                   ) : undefined
                 }>
                  {getDividerText(skillPath.skillPathName, sg.skillGroupName, skill.skillName)}
                  {skill.skillDescription && (
                      <p className="m-t--75 m-b-0 fnt-sz-sml-med fnt-wght-400">{skill.skillDescription}</p>
                    )}
                  
                </AccordionTrigger>
                
                <AccordionContent color={getAccordionColor(skillPath.skillPathId)}>
                  <div className="core-desktop-max-width p-t--75 p-b-1">
                                        
                    {skillPath.skillLevels?.map((level: ISkillLevel) => (
                      <div key={level.skillLevelId} className="mobile-skill-levels-container m-t-1">
                        <div className="dis-flex dis-flex-rows dis-flex-aln-center gap--50 m-b--50">
                          <LevelIcon
                            iconName={getLevelIconName(level.indexNumber)}
                            iconClassNames={['icon-primary', 'wdth-1-5', 'hgt-1-5']}
                            levelText={level.skillLevelName}
                            levelTextClassNames={['fnt-wght-900', 'fnt-sz-med']}
                            detailText=""
                            detailTextClassNames={['fnt-wght-500', 'fnt-sz-sml-med']}
                            iconColor="#0b254d"
                          />
                        </div>
                        <div className='m-t--75'>
                          {getSkillDetails(skill, level)}
                        </div>
                        <SkillLevelCheckboxes
                          level={level}
                          skill={skill}
                          skillLevels={skillPath.skillLevels ?? []}
                          isManager={true}
                          currentUpn={upn}
                          employeeProgressionLevels={progressionLevels}
                          employeeProgressionLevelsLength={progressionLevels.length}
                          allReadonly={true}
                        />
                      </div>
                    ))}
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
           
            </div>
          ))
        )
      )}

      {isPeriodEditable && skillTreeLoaded && skillTree.length > 0 && Array.from({ length: 8 - skillTree.length }).map((_, i) => (
        <div
          key={`empty-slot-${i}`}
          className="dis-flex dis-flex-rows dis-flex-aln-center dis-flex-jst-spc-btwn"
          style={{
            borderBottom: 'solid 2px #adb5bd',
            padding: '1.25rem',
          }}
        >
          
          <button
            className="btn-secondary dis-flex dis-flex-rows dis-flex-aln-center gap--25"
            style={{ padding: '4px 12px', fontSize: '0.85rem' }}
            onClick={handleAdd}
          >
            <Icon name="icon-plus" width={14} height={14} />
            <span>Add Competency</span>
          </button>
        </div>
      ))}
      </div>

      <Loading isLoading={loading} />
    </>
  );
};

export default SupervisorEmpCompetencies;
